import _ from 'lodash';
import VError from 'verror';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { getAdvertisers, getClientGrouping, getClientMapping, getDraftErrors } from './selectors';
import {
    getRevenueModelDefaults,
    getRevenueModelOptions,
    RevenueModelMapping,
} from 'states/resources/campaigns/business-logic';
/** import multi-line application imports in the section above, Sort Lines Ascending */

import { actions } from './reducer';
import { createHttp, getNextBase } from 'utils/http';
import { fetchOverviewPageGraphqlData } from 'pages/campaigns/campaigns-overview/actions';
import { getDefaults } from 'forms/campaign-form/services';
import { getEnvironmentSettings } from 'services/environment';
import { GraphQLClient } from 'graphql-request';
import { initializeConversionsDraft } from 'pages/campaigns/conversions-overview/actions';
import { isInternalUser } from 'states/profile/business-rules';
import Ads from 'states/resources/ads/actions';
import c from 'common/constants/flux-events';
import Campaigns from 'states/resources/campaigns/actions';
import notify from 'utils/notify';
/** import single-line application imports in the section above, Sort Lines Ascending */

const defaultsCampaignDraft = {
    ...getDefaults('payload_formData'),
    ...getDefaults('payload_createOnly'),
};

const initMw = () => ({ dispatch, getState }) => next => async action => {
    next(action);

    if (action.type === actions.init.type) {
        const { campaignId } = action.payload;

        const data = await fetchFormData();

        let draft;
        let flights = [];
        if (campaignId) {
            draft = await getDraftForExistingCampaign({ dispatch, getState, campaignId });
            flights = await fetchCampaignFlightMetadata(campaignId);
        } else {
            draft = await getDraftForNewCampaign({ dispatch, getState });
        }

        dispatch(
            actions.initSuccess({
                campaign: draft,
                ...data,
                flights,
            })
        );

        return draft;
    }
};

const submitMw = () => ({ dispatch, getState }) => next => async action => {
    next(action);

    if (action.type === actions.submit.type) {
        const campaignId = action.payload;
        const onSubmit = action.meta;

        const errors = getDraftErrors(getState());

        if (_.size(errors) > 0) {
            if (window.DEBUG) {
                console.log('errors', errors);
            }
            dispatch(actions.submitClientErrorsUnresolved(errors));
            return;
        }

        const draft = _.get(getState(), `campaignForm.draft`, []);

        try {
            let campaign;
            if (campaignId) {
                campaign = await updateCampaign({ dispatch, getState, campaignId, draft });
            } else {
                campaign = await createCampaign({ dispatch, draft });
            }

            if (onSubmit) {
                onSubmit(campaign);
            }
        } catch (err) {
            dispatch(actions.submitServerErrors(err));
        }
    }
};

const advertiserCreatedMw = () => ({ dispatch, getState }) => next => async action => {
    next(action);
    if (action.type === 'ADVERTISER_FORM__CREATE__SUCCESS') {
        try {
            // Extract the newly created organization from the action payload
            const { newOrganization } = action.payload;

            if (newOrganization) {
                // Append the new organization to campaign form state
                dispatch(actions.appendOrganization({ organization: newOrganization }));

                // Reuse the same logic as handleClientChange in campaign-form/index.js
                const state = getState();
                const draft = _.get(state, 'campaignForm.draft');

                const { clients, revenueModel, billingRateChange } = computeClientChanges({
                    selectedClients: [newOrganization.id],
                    clientType: 'advertiser',
                    isEditMode: false,
                    draft,
                    advertisers: getAdvertisers(state),
                    clientGrouping: getClientGrouping(state),
                    clientMapping: getClientMapping(state),
                });

                if (revenueModel !== undefined) {
                    dispatch(actions.changeRevenueModel(revenueModel));
                }

                if (billingRateChange !== undefined) {
                    dispatch(
                        actions.campaignFormUpdate({
                            billing_rate: billingRateChange,
                        })
                    );
                }

                dispatch(actions.campaignFormUpdate({ clients }));
            }
        } catch (err) {
            console.error('Error processing new advertiser:', err);
        }
    }
};

async function fetchFormData() {
    try {
        const http = createHttp();
        const query = `
            query getCampaignFormData {
                ftaLocationLists {
                    id
                    name
                    status
                }
            }
        `;

        const variables = {};

        const data = await http.graphql(query, variables);

        return data;
    } catch (err) {
        notify({
            error: new VError(err, 'Failed to fetch data for Campaign Form'),
        });
    }
}

async function fetchCampaignFlightMetadata(campaignId) {
    try {
        const http = createHttp();
        const query = `
            query getCampaignFlightMetadata($campaignId: Int) {
                campaign(id: $campaignId) {
                    flights {
                        metadata {
                            flightId
                            metrics {
                                impressions
                                owner_total_media_cost_local
                                billings_local
                            }
                        }
                    }
                }
            }
        `;

        const variables = {
            campaignId: Number(campaignId),
        };

        const {
            campaign: { flights },
        } = await http.graphql(query, variables);

        return _.map(flights, flight => {
            // Handle null metadata gracefully
            const metadata = flight?.metadata || {};
            const metrics = metadata?.metrics || {};

            return {
                flightId: metadata.flightId || null,
                owner_total_media_cost_local: metrics.owner_total_media_cost_local || 0,
                billings_local: metrics.billings_local || 0,
                impressions: metrics.impressions || 0,
            };
        });
    } catch (err) {
        notify({
            error: new VError(err, 'Failed to fetch data for Campaign Form'),
        });
        // Return empty array instead of undefined to prevent crashes
        return [];
    }
}

async function getDraftForExistingCampaign({ dispatch, getState, campaignId }) {
    try {
        const [campaign, ads, { organizations, ownOrganization }] = await Promise.all([
            dispatch(Campaigns.get(campaignId)),
            dispatch(Ads.getAll(campaignId)),
            getOrganizations({ getState, filters: { deprecated: false } }),
        ]);

        const currentClientIds = _.map(campaign.clients, 'organization');

        // First, non-deprecated organizations and own organization
        let allOrganizations = organizations;

        // Check if we are missing any current clients
        const fetchedIds = _.keys(allOrganizations);
        const missingIds = _.difference(currentClientIds, fetchedIds);

        if (!_.isEmpty(missingIds)) {
            // Fetch the missing current clients (deprecated ones)
            const filters = { organizationIds: missingIds };
            const { organizations: missingOrgs } = await getOrganizations({ getState, filters });

            // Merge them in
            allOrganizations = { ...allOrganizations, ...missingOrgs };
        }

        dispatch(
            actions.loadOrganizations({
                organizations: allOrganizations,
                ownOrganization,
            })
        );

        const currency = ownOrganization.currency || defaultsCampaignDraft.currency;
        const owner = ownOrganization.id;

        const campaignAttr_form = {
            ...defaultsCampaignDraft, // so that attr form does not has missing field
            currency,
            owner,
            ...campaign,
            ads,
        };

        return campaignAttr_form;
    } catch (error) {
        dispatch({
            type: c.OVERVIEW__CAMPAIGN_DRAFT__FETCH_ORGANIZATIONS_FAIL,
            payload: {
                error,
            },
        });

        console.error(error);
    }
}

/**
 * Fetches the draft for a new campaign.
 * - Fetches only non-deprecated organizations.
 *
 * @param {object} params
 * @param {Function} params.dispatch
 * @param {Function} params.getState
 */
async function getDraftForNewCampaign({ dispatch, getState }) {
    try {
        const { ownOrganization } = await loadOrganizationsWithDispatch({
            dispatch,
            getState,
            filters: { deprecated: false },
        });

        const environmentSettings = getEnvironmentSettings();

        const currency = ownOrganization.currency || environmentSettings.getDefaultCurrency();
        const default_timezone =
            ownOrganization.default_timezone || defaultsCampaignDraft.default_timezone;
        const owner = ownOrganization.id;

        let campaignAttr_form = {
            ...defaultsCampaignDraft, // so that attr form does not has missing field
            currency,
            default_timezone,
            owner,
            custom_fields: ownOrganization.custom_fields,
        };

        const revenueModelDefaults = getRevenueModelDefaults(ownOrganization.revenueModel);

        campaignAttr_form = {
            ...campaignAttr_form,
            ...revenueModelDefaults,
        };
        campaignAttr_form.creatorId = _.get(getState(), 'profile.userId');
        campaignAttr_form.revenueModel = ownOrganization.revenueModel || null;

        if (
            campaignAttr_form.billing_rate === 0 &&
            campaignAttr_form.revenueModel === 'agencyMargin'
        ) {
            campaignAttr_form.billing_rate = ownOrganization.agencyMarginRate;
        }

        // Full-serve should use old FTA pixel method. Self-serve should use new automated FTA method
        campaignAttr_form.fta_version = 2;
        campaignAttr_form.fta_enabled = false;
        campaignAttr_form.fta_management_level = null;
        campaignAttr_form.fta_location_list = null;
        campaignAttr_form.fta_partner_id = ownOrganization.fta_partner_id;

        if (isInternalUser()) {
            campaignAttr_form.isUsingFtaFullyManagedFields = true;
        }

        if ('623c83c69d8447ab615c7c7d' === ownOrganization.id) {
            campaignAttr_form.isUsingFtaLimitedFields = true;
        }

        // Make sure to initialize the campaign_primary_pacing field here
        // This is because the field doesn't exist in the UI, it's just set
        // when changing the revenue model or budget allocation method.
        campaignAttr_form.campaign_primary_pacing = 'spend';
        campaignAttr_form.campaign_max_total_impressions = 0;

        return campaignAttr_form;
    } catch (error) {
        dispatch({
            type: c.NEW_CAMPAIGN__DRAFT__INIT_FAIL,
            payload: {
                error,
            },
        });

        console.error(error);
    }
}

/**
 * Fetches child organizations via GraphQL.
 * Supports a specialized filter object passed as variables.
 * This is a pure data-fetching function without side effects.
 *
 * @param {object} params
 * @param {Function} params.getState - Redux getState function.
 * @param {object} [params.filters] - Filters for the organizations query (e.g. { deprecated: false, organizationIds: [... ] }).
 * @returns {Promise<{organizations: object, ownOrganization: object}>} Object containing formatted organizations map and own organization.
 *
 * `ownOrganization.users` includes `name` (Assignee dropdown label) and `third_party`
 * (when `true`, user is excluded from Assignee unless originally assigned).
 */
async function getOrganizations({ getState, filters = {} }) {
    try {
        const http = createHttp();
        const graphqlUrl = getNextBase() + 'graphql';

        const client = new GraphQLClient(graphqlUrl, {
            headers: {
                ...http._getHeaders(),
                Authorization: _.get(getState(), 'profile.authToken.auth_token'),
            },
        });

        const orgFilters = {
            relationship: 'child',
            type: ['client', 'co_managed_client'],
        };

        if (filters.deprecated !== undefined) {
            orgFilters.deprecated = filters.deprecated;
        }
        if (filters.organizationIds && filters.organizationIds.length) {
            orgFilters.organizationIds = filters.organizationIds;
        }

        const query = `
            query getOrganizations($filters: OrganizationFilters) {
                organizations(filters: $filters) {
                    id
                    name
                    currency
                    default_timezone
                    custom_fields {
                        name
                        value
                        required
                        key
                    }
                    type
                    client_type
                    revenueModel
                    ftaEnabled
                    fta_partner_id
                    agencyMarginRate
                    deprecated
                }
                ownOrganization {
                    id
                    type
                    fta_partner_id
                    agencyMarginRate
                    revenueModel
                    users {
                        id
                        name
                        first_name
                        last_name
                        suspended
                        global_role
                        deprecated
                        third_party
                    }
                    ftaEnabled
                    currency
                    default_timezone
                    custom_fields {
                        name
                        value
                        required
                        key
                    }
                }
            }
        `;

        const variables = {
            filters: orgFilters,
        };

        const { organizations, ownOrganization } = await client.request(query, variables);

        let formattedOrganizations = {};
        _.each(organizations, org => {
            formattedOrganizations[org.id] = {
                id: org.id,
                attr: org,
            };
        });

        const { id, users } = ownOrganization;
        formattedOrganizations[id] = {
            ...formattedOrganizations[id],
            attr: {
                ...formattedOrganizations[id].attr,
                users,
            },
        };

        return { organizations: formattedOrganizations, ownOrganization };
    } catch (err) {
        console.error(`GET ALL organizations error`, err);

        throw err;
    }
}

/**
 * Top-level function that fetches organizations and dispatches them to the store.
 *
 * @param {object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} params.getState - Redux getState function.
 * @param {object} [params.filters] - Filters for the organizations query (e.g. { deprecated: false, organizationIds: [... ] }).
 * @returns {Promise<{organizations: object, ownOrganization: object}>} Object containing formatted organizations map and the own organization.
 */
async function loadOrganizationsWithDispatch({ dispatch, getState, filters = {} }) {
    try {
        const { organizations, ownOrganization } = await getOrganizations({
            getState,
            filters,
        });

        dispatch(actions.loadOrganizations({ organizations, ownOrganization }));

        return { organizations, ownOrganization };
    } catch (err) {
        console.error(`Load organizations with dispatch error`, err);
        throw err;
    }
}

async function createCampaign({ dispatch, draft }) {
    return dispatch(Campaigns.create(draft)).then(
        response => {
            const campaign = response;

            dispatch(actions.submitSuccess());

            return campaign;
        },
        error => {
            // throw toaster error for server errors
            dispatch({
                type: c.NEW_CAMPAIGN__DRAFT__SUBMIT_FAIL,
                error: error.body,
            });

            return Promise.reject(error.body);
        }
    );
}

async function updateCampaign({ dispatch, campaignId, draft }) {
    if (!campaignId) {
        throw new Error('Edit campaign campaignId not found');
    }

    dispatch({
        type: c.OVERVIEW__CAMPAIGN_DRAFT__SUBMIT,
        payload: { campaignId, draft },
    });

    return dispatch(Campaigns.update(campaignId, draft)).then(
        () => {
            dispatch(actions.submitSuccess());
            dispatch(Ads.getAll(campaignId));
            dispatch(initializeConversionsDraft(campaignId));
            dispatch(fetchOverviewPageGraphqlData(Number(campaignId)));
        },
        error => {
            dispatch({
                type: c.OVERVIEW__CAMPAIGN_DRAFT__SUBMIT_FAIL,
                payload: { campaignId },
                error: error.body,
            });

            return Promise.reject(error.body);
        }
    );
}

export const makeMiddlewares = deps => {
    return [initMw(deps), submitMw(deps), advertiserCreatedMw(deps)];
};
export const middlewares = makeMiddlewares({});

/**
 * Pure function that computes the state changes for a client selection.
 * Extracted from CampaignForm.handleClientChange so it can be reused
 * from both the component and the middleware.
 *
 * @param {object}   params
 * @param {string[]} params.selectedClients - Array of organization IDs being selected.
 * @param {string}   params.clientType - One of 'advertiser', 'partner', 'sales_rep'.
 * @param {boolean}  params.isEditMode - Whether the campaign is being edited.
 * @param {object}   params.draft - The current campaign draft.
 * @param {Array}    params.advertisers - Advertiser options (from selector/props).
 * @param {object}   params.clientGrouping - Current client grouping (from selector/props).
 * @param {object}   params.clientMapping - Current client mapping (from selector/props).
 * @returns {{ clients: Array, revenueModel?: string|null, billingRateChange?: number }}
 */
export function computeClientChanges({
    selectedClients,
    clientType,
    isEditMode,
    draft,
    advertisers,
    clientGrouping,
    clientMapping,
}) {
    const result = {};

    if (clientType === 'advertiser' && !isEditMode) {
        const validRevenueModels = getRevenueModelOptions(draft).map(option => option.value);

        _.each(advertisers, advertiser => {
            if (advertiser.value === _.head(selectedClients)) {
                let newRevenueModel = null;
                if (!advertiser.revenueModel) {
                    newRevenueModel = RevenueModelMapping.Disabled;
                } else if (validRevenueModels.includes(advertiser.revenueModel)) {
                    newRevenueModel = advertiser.revenueModel;
                }
                result.revenueModel = newRevenueModel;

                if (
                    advertiser.revenueModel &&
                    advertiser.revenueModel === RevenueModelMapping.AgencyMargin
                ) {
                    result.billingRateChange = advertiser.agencyMarginRate;
                }
            }
        });

        if (_.head(selectedClients) === '') {
            result.revenueModel = RevenueModelMapping.Disabled;
        }
    }

    const nextGrouping = {
        ...clientGrouping,
        [clientType]: _.map(selectedClients, organizationId => ({
            organization: organizationId,
            shared: _.get(clientMapping, `${organizationId}.shared`, false),
            type: clientType,
        })),
    };

    result.clients = _(nextGrouping)
        .map(clients => clients)
        .flatten()
        .value()
        .filter(c => c.organization !== '');

    return result;
}
